import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Bell, X, ChevronRight, Check, Tag, Megaphone, Package, Trash2, Coffee, ArrowRight } from 'lucide-react';
import { Card } from './ui/card';
import { Skeleton } from './ui/skeleton';
import { motion, AnimatePresence, useMotionValue, animate } from 'framer-motion';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// iOS Mail-style swipe-to-delete geometry. Swiping left past the reveal point
// snaps the row to a rest position showing a red "Delete" button — deletion
// always requires an explicit tap on that button (delete is irreversible and
// there's no undo, so we never delete on the swipe itself / a fast flick). Only
// one row can be open at a time (the parent owns `revealedId`), and the red
// affordance fills the full row width behind the card so an over-swipe can't
// expose an empty gap.
const SWIPE_REVEAL_PX = 88; // rest offset that exposes the Delete button

/**
 * Fire an iOS/Android haptic impact via `@capacitor/haptics` (lazy-imported so
 * the web bundle stays clean). `style` is case-insensitive 'light'|'medium'|
 * 'heavy'. Silent no-op on web / PWA. Requires the Haptics plugin in the native
 * build (added 2026-05; reaches installed apps only via a new IPA/AAB).
 */
async function tapHaptic(style = 'light') {
  try {
    if (typeof window === 'undefined') return;
    if (!window.Capacitor?.isNativePlatform?.()) return;
    const { Haptics, ImpactStyle } = await import('@capacitor/haptics');
    const map = {
      light: ImpactStyle.Light,
      medium: ImpactStyle.Medium,
      heavy: ImpactStyle.Heavy,
    };
    await Haptics.impact({ style: map[String(style).toLowerCase()] || ImpactStyle.Light });
  } catch {
    /* defensive — never block UI */
  }
}

const NOTIF_ICON_MAP = {
  offer: Tag,
  announcement: Megaphone,
  order_update: Package,
  general: Bell,
};

function getNotifIcon(type) {
  return NOTIF_ICON_MAP[type] || Bell;
}

function groupByDate(notifications) {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  const startOfWeek = new Date(today);
  startOfWeek.setDate(today.getDate() - today.getDay());

  const buckets = [
    { label: 'Today', items: [] },
    { label: 'Yesterday', items: [] },
    { label: 'Earlier This Week', items: [] },
    { label: 'Older', items: [] },
  ];

  for (const n of notifications) {
    const d = new Date(n.created_at);
    const nDate = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    if (nDate >= today) buckets[0].items.push(n);
    else if (nDate >= yesterday) buckets[1].items.push(n);
    else if (nDate >= startOfWeek) buckets[2].items.push(n);
    else buckets[3].items.push(n);
  }

  return buckets.filter(b => b.items.length > 0);
}

/**
 * Bell icon with unread badge and full-height side drawer; polls unread count
 * every 15 s. Visible to all signed-in users (including admins so they can
 * verify their own test sends).
 */
export default function NotificationBell() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  // id of the single row currently swiped open (showing its Delete button).
  // Lifted here so opening one row auto-closes any other.
  const [revealedId, setRevealedId] = useState(null);
  // The notification opened in the detail sheet (a tap on a row with no destination).
  const [detail, setDetail] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  // Pause the 15s unread poll while the drawer is open so a tick can't
  // re-show the badge after we auto-mark everything read on open.
  useEffect(() => {
    if (!user || open) return;
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 15000);
    return () => clearInterval(interval);
  }, [user, token, open]);

  // Lock background scroll while the drawer is open (this is a framer-motion
  // drawer, not Radix, so it has no built-in lock). Restore the PRIOR overflow
  // value on close/unmount rather than hard-resetting to ''.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  const fetchUnreadCount = async () => {
    try {
      const res = await axios.get(`${API}/notifications/me/unread-count`, { headers });
      setUnreadCount(res.data.count);
    } catch {}
  };

  const fetchNotifications = async () => {
    try {
      const res = await axios.get(`${API}/notifications/me`, { headers });
      setNotifications(res.data);
    } catch {
    } finally {
      setLoading(false);
    }
  };

  /**
   * Open the drawer, show the loading skeleton, load notifications, then
   * auto-mark everything read when there were unread items (Business Rule #13:
   * this only flips read=false→true server-side, push + bell stay in sync).
   */
  const handleOpen = async () => {
    setOpen(true);
    setLoading(true);
    const hadUnread = unreadCount > 0;
    await fetchNotifications();
    if (hadUnread) {
      handleMarkAllRead();
    }
  };

  const handleNotificationClick = async (notif) => {
    if (!notif.is_read) {
      await axios.patch(`${API}/notifications/me/${notif.id}/read`, {}, { headers }).catch(() => {});
      setUnreadCount(prev => Math.max(0, prev - 1));
      setNotifications(prev => prev.map(n => n.id === notif.id ? { ...n, is_read: true } : n));
    }
    if (notif.link) {
      followLink(notif.link);
    } else {
      // Nowhere to go: open the message itself instead of doing nothing.
      setDetail(notif);
    }
  };

  const followLink = (link) => {
    setDetail(null);
    setOpen(false);
    if (link.startsWith('http')) {
      window.open(link, '_blank');
    } else {
      navigate(link);
    }
  };

  const handleDismiss = async (notifId) => {
    const notif = notifications.find(n => n.id === notifId);
    setRevealedId(cur => (cur === notifId ? null : cur));
    setNotifications(prev => prev.filter(n => n.id !== notifId));
    if (notif && !notif.is_read) {
      setUnreadCount(prev => Math.max(0, prev - 1));
    }
    await axios.delete(`${API}/notifications/me/${notifId}`, { headers }).catch(() => {});
  };

  const handleMarkAllRead = async () => {
    await axios.patch(`${API}/notifications/me/read-all`, {}, { headers }).catch(() => {});
    setUnreadCount(0);
    setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
  };

  if (!user) return null;

  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 7) return `${days}d ago`;
    return new Date(dateStr).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
  };

  const groups = groupByDate(notifications);

  return (
    <>
      {/* Bell trigger */}
      <button onClick={handleOpen} className="relative p-1" data-testid="notification-bell">
        <Bell className="w-5 h-5" strokeWidth={1.75} />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-accent text-accent-foreground text-[10px] font-bold flex items-center justify-center shadow-sm ring-2 ring-background">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Drawer */}
      <AnimatePresence>
        {open && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 bg-foreground/50 backdrop-blur-sm z-[80]"
              onClick={() => setOpen(false)}
            />

            {/* Panel */}
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 28, stiffness: 320 }}
              className="fixed top-0 right-0 bottom-0 w-full max-w-md bg-background z-[90] flex flex-col shadow-2xl"
              data-testid="notification-drawer"
            >
              {/* Dark header */}
              <div className="bg-primary text-primary-foreground px-4 pb-4 flex flex-col gap-2 app-header-safe">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2.5">
                    <div className="w-9 h-9 rounded-full bg-accent/20 flex items-center justify-center">
                      <Bell className="w-4 h-4 text-accent" strokeWidth={1.5} />
                    </div>
                    <div className="flex flex-col">
                      <h2 className="text-lg font-bold font-fraunces tracking-tight leading-none">Notifications</h2>
                      <p className="text-[11px] text-primary-foreground/60 mt-0.5">
                        {unreadCount > 0
                          ? `${unreadCount} unread`
                          : notifications.length > 0
                            ? 'All caught up'
                            : 'Updates from Penguin Bean'}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setOpen(false)}
                    className="p-1.5 -mr-1 rounded-full hover:bg-white/10 text-primary-foreground/80 hover:text-primary-foreground transition-colors"
                    data-testid="close-notifications"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* List */}
              <div className="flex-1 overflow-y-auto bg-muted/30 native:pb-[env(safe-area-inset-bottom)]">
                {loading ? (
                  <div className="p-3 space-y-2.5">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <NotificationRowSkeleton key={i} />
                    ))}
                  </div>
                ) : notifications.length === 0 ? (
                  <EmptyState />
                ) : (
                  <div className="p-3">
                    {groups.map(group => (
                      <div key={group.label}>
                        <div className="px-1 pt-3 pb-1.5 first:pt-0">
                          <h4 className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground/70">
                            {group.label}
                          </h4>
                        </div>
                        <div className="space-y-2.5">
                          <AnimatePresence>
                            {group.items.map((notif, idx) => (
                              <NotificationCard
                                key={notif.id}
                                notif={notif}
                                index={idx}
                                onClick={() => handleNotificationClick(notif)}
                                onDismiss={handleDismiss}
                                timeAgo={timeAgo}
                                expanded={expandedId === notif.id}
                                onToggleExpand={(id) =>
                                  setExpandedId((cur) => (cur === id ? null : id))
                                }
                                isRevealed={revealedId === notif.id}
                                onReveal={() => setRevealedId(notif.id)}
                                onCloseReveal={() =>
                                  setRevealedId((cur) => (cur === notif.id ? null : cur))
                                }
                              />
                            ))}
                          </AnimatePresence>
                        </div>
                      </div>
                    ))}
                    {notifications.length > 0 && notifications.length <= 3 && (
                      <DiscoverSection onNavigate={(path) => { setOpen(false); navigate(path); }} />
                    )}
                  </div>
                )}
              </div>

              <NotificationDetailSheet
                notif={detail}
                onClose={() => setDetail(null)}
                onOpenLink={followLink}
                timeAgo={timeAgo}
              />

              {/* Footer */}
              {unreadCount > 0 && (
                <button
                  onClick={handleMarkAllRead}
                  className="bg-accent text-accent-foreground py-3 px-4 flex items-center justify-center gap-2 text-sm font-bold hover:bg-accent/90 transition-colors"
                  style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 12px)' }}
                  data-testid="mark-all-read"
                >
                  <Check className="w-4 h-4" />
                  Mark all read
                </button>
              )}
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}

/**
 * Skeleton placeholder row mirroring NotificationCard's geometry — shown while
 * the list loads so we never flash a false "all caught up" state.
 */
function NotificationRowSkeleton() {
  return (
    <Card className="overflow-hidden bg-card">
      <div className="p-3.5 flex gap-3 items-start">
        <Skeleton className="w-9 h-9 rounded-full flex-shrink-0" />
        <div className="flex-1 min-w-0 space-y-2">
          <Skeleton className="h-4 w-2/3" />
          <Skeleton className="h-3 w-full" />
          <Skeleton className="h-2.5 w-16 mt-2" />
        </div>
      </div>
    </Card>
  );
}

function NotificationCard({ notif, index, onClick, onDismiss, timeAgo, expanded, onToggleExpand, isRevealed, onReveal, onCloseReveal }) {
  const isUnread = !notif.is_read;
  const Icon = getNotifIcon(notif.type);
  const x = useMotionValue(0);
  const isLong = (notif.message || '').length > 120;
  // The Delete button only "arms" a beat after the row settles open, so the
  // trailing click from the reveal swipe itself can't fire it — deletion is
  // always a deliberate second tap on the button.
  const [armed, setArmed] = useState(false);

  const SNAP = { type: 'spring', stiffness: 500, damping: 40 };

  // Reveal is controlled by the parent (`revealedId`) so opening one row closes
  // any other. We animate the SAME motion value the drag writes to (`x`) —
  // using `animate(x, …)` rather than a separate `useAnimationControls` avoids
  // the framer drag-vs-animate conflict that could leave the row un-slid (x:0)
  // while still marked open, showing the red layer through the whole card.
  useEffect(() => {
    let t;
    const anim = animate(x, isRevealed ? -SWIPE_REVEAL_PX : 0, SNAP);
    if (isRevealed) {
      setArmed(false);
      t = setTimeout(() => setArmed(true), 300);
    } else {
      setArmed(false);
    }
    return () => { anim.stop(); clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRevealed]);

  const del = () => {
    if (!armed) return;
    tapHaptic('medium');
    onDismiss(notif.id);
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ x: -400, opacity: 0 }}
      transition={{ delay: Math.min(index * 0.03, 0.18), duration: 0.2 }}
      className="relative overflow-hidden rounded-xl"
    >
      {/* Red Delete affordance — fills the full row behind the card so an
          over-swipe never exposes a gap; the Delete target sits at the right.
          Delete is irreversible, so it only fires on an explicit tap here. */}
      <button
        type="button"
        onClick={del}
        disabled={!armed}
        aria-label="Delete notification"
        tabIndex={isRevealed && armed ? 0 : -1}
        className={`absolute inset-0 flex items-center justify-end bg-red-500 text-white active:bg-red-600 rounded-xl ${armed ? '' : 'pointer-events-none'}`}
      >
        <span
          className="flex flex-col items-center justify-center gap-0.5"
          style={{ width: SWIPE_REVEAL_PX }}
        >
          <Trash2 className="w-5 h-5" strokeWidth={1.75} />
          <span className="text-[10px] font-semibold">Delete</span>
        </span>
      </button>

      {/* Draggable card */}
      <motion.div
        drag="x"
        dragDirectionLock
        dragConstraints={{ left: -SWIPE_REVEAL_PX, right: 0 }}
        dragElastic={{ left: 0.04, right: 0 }}
        dragMomentum={false}
        style={{ x }}
        onDragEnd={(event, info) => {
          // A real left swipe rests the row open showing Delete; anything
          // shorter snaps it closed. We NEVER delete on the swipe — deletion is
          // a deliberate tap on the (briefly-disarmed) Delete button.
          if (info.offset.x < -SWIPE_REVEAL_PX / 2 || info.velocity.x < -300) {
            if (!isRevealed) tapHaptic('light');
            onReveal();
          } else {
            onCloseReveal();
          }
        }}
        className="relative z-10 bg-card"
      >
        <Card
          className={`overflow-hidden cursor-pointer transition-all ${
            isUnread
              ? 'border-l-4 border-l-accent shadow-sm bg-card'
              : 'bg-card hover:bg-muted/30'
          }`}
          onClick={() => { if (isRevealed) { onCloseReveal(); } else { onClick(); } }}
          data-testid={`notification-item-${notif.id}`}
        >
          <div className="p-3.5 flex gap-3 items-start">
            <div
              className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${
                isUnread ? 'bg-accent/15 text-accent' : 'bg-muted text-muted-foreground'
              }`}
            >
              <Icon className="w-4 h-4" strokeWidth={1.5} />
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2 mb-0.5">
                <h3
                  className={`font-fraunces leading-tight ${
                    isUnread ? 'text-base font-bold text-foreground' : 'text-sm font-semibold text-foreground/80'
                  }`}
                >
                  {notif.title}
                </h3>
                {isUnread && (
                  <span className="w-2 h-2 rounded-full bg-accent flex-shrink-0 mt-1.5" aria-hidden />
                )}
              </div>
              {notif.message && (
                <p className={`text-xs text-muted-foreground leading-relaxed ${expanded ? '' : 'line-clamp-2'}`}>
                  {notif.message}
                </p>
              )}
              {isLong && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleExpand(notif.id);
                  }}
                  className="text-[11px] font-semibold text-accent hover:underline mt-0.5"
                >
                  {expanded ? 'Show less' : 'Read more'}
                </button>
              )}
              <div className="mt-2 pt-2 border-t border-dashed border-border/60">
                <span className="text-[10px] text-muted-foreground font-medium">
                  {timeAgo(notif.created_at)}
                </span>
              </div>
            </div>
          </div>
        </Card>
      </motion.div>
    </motion.div>
  );
}

/**
 * Bottom sheet with the whole message — title, full body, when — for a row
 * that has no destination (a tap on one with a destination navigates instead).
 * Framer-motion like the drawer itself, so it layers above the drawer's
 * z-[90]; a Radix sheet's z-50 overlay would land behind it.
 */
function NotificationDetailSheet({ notif, onClose, onOpenLink, timeAgo }) {
  const Icon = getNotifIcon(notif?.type);
  return (
    <AnimatePresence>
      {notif && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="absolute inset-0 bg-foreground/40 z-[95]"
            onClick={onClose}
          />
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 340 }}
            className="absolute inset-x-0 bottom-0 z-[96] bg-background rounded-t-3xl shadow-2xl max-h-[85%] flex flex-col"
            style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
            data-testid="notification-detail"
          >
            <div className="mx-auto mt-3 h-1.5 w-10 rounded-full bg-foreground/30" />
            <div className="px-5 pt-3 pb-5 overflow-y-auto">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-accent/15 text-accent flex items-center justify-center flex-shrink-0">
                  <Icon className="w-5 h-5" strokeWidth={1.5} />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-fraunces text-lg font-bold leading-tight">{notif.title}</h3>
                  <p className="text-[11px] text-muted-foreground mt-0.5">{timeAgo(notif.created_at)}</p>
                </div>
                <button onClick={onClose} className="p-1.5 -mr-1.5 rounded-full hover:bg-muted" aria-label="Close" data-testid="notification-detail-close">
                  <X className="w-5 h-5" />
                </button>
              </div>
              {notif.message && (
                <p className="text-sm text-foreground/90 leading-relaxed mt-4 whitespace-pre-line">{notif.message}</p>
              )}
              {notif.link && (
                <button
                  onClick={() => onOpenLink(notif.link)}
                  className="mt-5 w-full flex items-center justify-center gap-1.5 rounded-full bg-primary text-primary-foreground px-4 py-3 text-sm font-bold active:scale-[0.98]"
                >
                  Open <ArrowRight className="w-4 h-4" strokeWidth={2} />
                </button>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

function DiscoverSection({ onNavigate }) {
  const ctas = [
    { label: "Browse today's offers", path: '/offer-of-the-day', icon: Tag, color: 'bg-accent/10 text-accent' },
    { label: 'Explore the menu', path: '/menu', icon: Coffee, color: 'bg-primary/10 text-primary' },
  ];
  return (
    <div className="pb-4 pt-2">
      <div className="border-t border-dashed border-border/60 pt-4">
        <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground/70 px-1 mb-3">
          While you're here
        </p>
        <div className="space-y-2">
          {ctas.map(cta => (
            <button
              key={cta.path}
              onClick={() => onNavigate(cta.path)}
              className="w-full flex items-center gap-3 p-3 rounded-xl bg-card border hover:bg-muted/30 transition-all group"
            >
              <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${cta.color}`}>
                <cta.icon className="w-4 h-4" strokeWidth={1.5} />
              </div>
              <span className="text-sm font-semibold text-foreground group-hover:text-primary transition-colors">
                {cta.label}
              </span>
              <ChevronRight className="w-4 h-4 text-muted-foreground ml-auto" />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center h-full px-8 text-center py-16">
      <div className="w-16 h-16 rounded-full bg-accent/10 flex items-center justify-center mb-4">
        <Bell className="w-7 h-7 text-accent" strokeWidth={1.5} />
      </div>
      <h3 className="text-lg font-bold font-fraunces text-foreground mb-1">You're all caught up</h3>
      <p className="text-sm text-muted-foreground leading-relaxed max-w-[260px]">
        New offers, order updates, and notes from the Penguin Bean team will land here.
      </p>
    </div>
  );
}
